import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { settingsActions } from 'saur-viseau-state-management/lib/settings/state';
import { isFeatureEnabled } from 'saur-viseau-state-management/lib/settings/selectors';
import { States as SettingsStates } from 'saur-viseau-state-management/lib';
import { handleLogout } from '../LogoutHelper';
import { getAuthProvider, refreshAccessToken } from './authProvider';
import { getLastActivityTime, getInactivityThresholdMs } from './useUserActivity';

/** Rafraîchit le token 60 secondes avant son expiration */
const REFRESH_BEFORE_EXPIRY_MS = 60 * 1000;

/**
 * Hook qui planifie automatiquement le rafraîchissement du token Keycloak
 * avant son expiration. Le refresh n'est autorisé que si l'utilisateur
 * a été actif (clic) dans les 30 dernières secondes avant l'expiration.
 *
 * @param logoutPath - Chemin complet de la page de login (ex: '/fr/login')
 * @param userType   - Type d'utilisateur pour le logout conditionnel
 */
function useRefreshToken(logoutPath: string, userType: string | null | undefined) {
    const dispatch = useDispatch();
    const isTokenDebugEnabled = useSelector((state: SettingsStates) =>
        isFeatureEnabled(state, 'Show-Token-Debug')
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        let timerId: ReturnType<typeof setTimeout>;

        const doRefresh = async () => {
            const logoutUrl = `${window.location.origin}${logoutPath}`;

            // Vérifier l'activité : l'utilisateur doit avoir cliqué
            // dans les INACTIVITY_LOGOUT_THRESHOLD_MS avant l'expiration
            const timeSinceClick = Date.now() - getLastActivityTime();
            const isUserActive = timeSinceClick < getInactivityThresholdMs();

            if (isTokenDebugEnabled) {
                const ssoNow = JSON.parse(localStorage.getItem('sso-response') ?? 'null');
                console.group('%c[useRefreshToken] Tentative de refresh proactif', 'color: #1976d2; font-weight: bold');
                console.log('[useRefreshToken] expires_at actuel  :', ssoNow?.expires_at
                    ? new Date(ssoNow.expires_at * 1000).toLocaleTimeString('fr-FR')
                    : 'N/A'
                );
                console.log('[useRefreshToken] Dernier clic il y a :', `${Math.round(timeSinceClick / 1000)}s`);
                console.log('[useRefreshToken] Éligible au refresh :', isUserActive ? '✅ OUI' : '❌ NON → logout');
                console.log('[useRefreshToken] id_token (avant)    :', ssoNow?.id_token ? `${ssoNow.id_token.slice(0, 20)}…` : '⚠️ absent');
                console.groupEnd();
            }

            if (!isUserActive) {
                handleLogout(dispatch, getAuthProvider(), logoutUrl, userType, 'useRefreshToken:inactif');
                return;
            }

            const tokens = await refreshAccessToken();
            if (tokens) {
                dispatch(settingsActions.setAccessToken(tokens.token));
                setTimeout(() => {
                    dispatch(settingsActions.setRefreshToken(tokens.refreshToken));
                }, 100);
                // Planifier le prochain rafraîchissement pour le nouveau token
                scheduleNextRefresh();
            } else {
                if (isTokenDebugEnabled) console.warn('[useRefreshToken] Refresh échoué → logout');
                handleLogout(dispatch, getAuthProvider(), logoutUrl, userType, 'useRefreshToken:refresh-echoue');
            }
        };

        const scheduleNextRefresh = () => {
            const ssoResponse = JSON.parse(localStorage.getItem('sso-response') ?? 'null');
            const expires_at = ssoResponse?.expires_at;
            if (!expires_at) return;

            const delay = Math.max(0, expires_at * 1000 - Date.now() - REFRESH_BEFORE_EXPIRY_MS);
            timerId = setTimeout(doRefresh, delay);
        };

        scheduleNextRefresh();

        return () => clearTimeout(timerId);
    }, []); // S'exécute une seule fois au montage
}

export { useRefreshToken };
